import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import z from 'zod';

import type { AccountDTO } from '@invenco/accounts-interface';

import { DialogBody, DialogDescription, DialogHeader, DialogTitle } from '@/components/core/dialog';
import { FormDialogContent } from '@/components/forms/form-dialog';
import { TextFormField } from '@/components/forms/text-form-field';
import type { Result } from '@/shared/helpers/Result';

const schema = z.object({
  name: z.string().min(1, 'Enter account name'),
  code: z.string().min(1, 'Enter account code'),
  primaryName: z.string().min(1, 'Enter contact name'),
  primaryEmail: z.string().email('Enter a valid email'),
  primaryPhone: z.string().min(1, 'Enter contact phone number'),
  externalId: z.string().optional().nullable(),
});

type Props = {
  account: Partial<AccountDTO>;
  isNew: boolean;
  onSubmit: (data: Partial<AccountDTO>) => Promise<Result> | Promise<void> | void;
};

export function AccountDetailsDialogContent({ account, isNew, onSubmit }: Props) {
  const defaultValues = useMemo(
    () => ({
      name: account.name ?? '',
      code: account.code ?? '',
      primaryName: account.primaryName ?? '',
      primaryEmail: account.primaryEmail ?? '',
      primaryPhone: account.primaryPhone ?? '',
      externalId: account.externalId ?? '',
    }),
    [
      account.code,
      account.externalId,
      account.name,
      account.primaryEmail,
      account.primaryName,
      account.primaryPhone,
    ],
  );

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues,
  });

  useEffect(() => {
    form.reset(defaultValues);
  }, [defaultValues, form]);

  return (
    <FormDialogContent
      form={form}
      onSubmit={async (data) => {
        await onSubmit({
          ...data,
          externalId: data.externalId === '' ? null : data.externalId,
        });
      }}
    >
      <DialogHeader>
        <DialogTitle>{isNew ? 'Add account' : 'Edit account'}</DialogTitle>
        <DialogDescription>Enter the account and primary contact details.</DialogDescription>
      </DialogHeader>
      <DialogBody>
        <TextFormField control={form.control} name="name" label="Account name" />
        <TextFormField control={form.control} name="code" label="Account code" disabled={!isNew} />
        <TextFormField control={form.control} name="primaryName" label="Contact name" />
        <TextFormField
          control={form.control}
          name="primaryEmail"
          label="Contact email"
          type="email"
        />
        <TextFormField control={form.control} name="primaryPhone" label="Contact phone" />
        <TextFormField control={form.control} name="externalId" label="Billing ID (external ID)" />
      </DialogBody>
    </FormDialogContent>
  );
}
